import { UNIT_KINDS, UNIT_STATUSES } from './read-api.js';
// Grid cells → unit values. Shared by the web client (immediate feedback) and the server
// (authoritative validation). Cells are plain text from the grid or from an Excel paste: they are
// trimmed and parsed as data only. Nothing is evaluated, and HTML is never interpreted.
export const UNIT_CELL_FIELDS = [
    'code',
    'displayNumber',
    'kind',
    'building',
    'floor',
    'modelType',
    'rooms',
    'areaM2',
    'status',
];
export const UNIT_KIND_LABELS = {
    apartment: 'דירה',
    parking: 'חניה',
    storage: 'מחסן',
    commercial: 'מסחרי',
};
export const UNIT_STATUS_LABELS = {
    planned: 'מתוכננת',
    active: 'פעילה',
    retired: 'לא פעילה',
};
/** Longest accepted cell text, before any field rule (protects every later step). */
export const MAX_CELL_LENGTH = 200;
const DECIMAL = /^\d{1,6}(\.\d{1,2})?$/;
const INTEGER = /^-?\d{1,3}$/;
function fromLabel(text, labels) {
    const lower = text.toLowerCase();
    return Object.keys(labels).find((key) => key === lower || labels[key] === text);
}
/**
 * Parses the given cells. In `create` mode every required field must be present; in `update`
 * mode only the given cells are parsed (they replace the stored values).
 */
export function parseUnitCells(cells, mode) {
    const values = {};
    const errors = {};
    const text = (field) => {
        const raw = cells[field];
        if (raw === undefined)
            return mode === 'create' ? '' : undefined;
        if (raw.length > MAX_CELL_LENGTH) {
            errors[field] = 'too_long';
            return undefined;
        }
        return raw.trim();
    };
    const requiredText = (field, max) => {
        const value = text(field);
        if (value === undefined)
            return;
        if (value === '')
            errors[field] = 'required';
        else if (value.length > max)
            errors[field] = 'too_long';
        else
            values[field] = value;
    };
    requiredText('code', 32);
    requiredText('displayNumber', 12);
    requiredText('building', 12);
    const kind = text('kind');
    if (kind !== undefined) {
        const parsed = fromLabel(kind, UNIT_KIND_LABELS);
        if (kind === '')
            errors.kind = 'required';
        else if (parsed === undefined)
            errors.kind = 'invalid';
        else
            values.kind = parsed;
    }
    const floor = text('floor');
    if (floor !== undefined) {
        if (floor === '')
            values.floor = null;
        else if (!INTEGER.test(floor) || Number(floor) < -20 || Number(floor) > 200)
            errors.floor = 'invalid';
        else
            values.floor = Number(floor);
    }
    const modelType = text('modelType');
    if (modelType !== undefined) {
        if (modelType.length > 40)
            errors.modelType = 'too_long';
        else
            values.modelType = modelType === '' ? null : modelType;
    }
    const rooms = text('rooms');
    if (rooms !== undefined) {
        const number = Number(rooms);
        if (rooms === '')
            values.rooms = null;
        else if (!DECIMAL.test(rooms) || number <= 0 || number > 20 || (number * 2) % 1 !== 0)
            errors.rooms = 'invalid';
        else
            values.rooms = number;
    }
    const area = text('areaM2');
    if (area !== undefined) {
        const number = Number(area);
        if (area === '')
            values.areaM2 = null;
        else if (!DECIMAL.test(area) || number <= 0 || number > 99_999.99)
            errors.areaM2 = 'invalid';
        else
            values.areaM2 = number;
    }
    const status = text('status');
    if (status !== undefined) {
        const parsed = fromLabel(status, UNIT_STATUS_LABELS);
        if (status === '') {
            if (mode === 'create')
                values.status = 'planned';
            else
                errors.status = 'required';
        }
        else if (parsed === undefined)
            errors.status = 'invalid';
        else
            values.status = parsed;
    }
    if (values.modelType != null && values.kind !== undefined && values.kind !== 'apartment') {
        errors.modelType = 'apartments_only';
    }
    return { values, errors };
}
/** Hebrew messages for cell error codes (UI only; the server sends codes). */
export const CELL_ERROR_MESSAGES = {
    required: 'שדה חובה',
    too_long: 'הערך ארוך מדי',
    invalid: 'ערך לא תקין',
    apartments_only: 'טיפוס מותר לדירות בלבד',
    unknown_building: 'בניין לא קיים בפרויקט',
    unknown_floor: 'קומה לא קיימת בבניין',
    duplicate_existing: 'כבר קיים בפרויקט',
    duplicate_batch: 'מופיע יותר מפעם אחת בשינויים',
    stale: 'הרשומה עודכנה על ידי משתמש אחר',
    missing: 'הרשומה אינה קיימת בפרויקט',
};
//# sourceMappingURL=unit-cells.js.map